import NavigationBar from "@/components/navigation-bar/NavigationBar.vue";
import AGDataTable from "@/components/AG-data-table/AGDataTable.vue";
import {columnHeaders} from "@/components/AG-data-table/columnHeaders/column-headers"
import {BookingOptionDropDown, BookingService} from "@/services/booking.service";
import {TimezoneService} from "@/services/timezone.service";
import {LocationDropdown, LocationService} from "@/services/location.service";
import moment from "moment/moment";
import InputText from "@/components/input-text/InputText.vue";
import InputDropdown from "@/components/input-dropdown/InputDropdown.vue";
import InputDatePicker from "@/components/input-date-picker/InputDatePicker.vue";
import InputCheckbox from "@/components/input-checkbox/InputCheckbox.vue";
import {GroupService} from "@/services/group.service";
import {EntityDropdown, EntityService} from "@/services/entity.service";
import InputDropdownCheckbox from "@/components/input-dropdown-checkbox/InputDropdownCheckbox.vue";
import router from '../../router'
import {CustomerDropdown, CustomerService} from "@/services/customer.service";
import {KeycloakService} from "@/services/keycloak.service";
import {AccountService} from "@/services/account.service";
import {CookieService} from "@/services/cookie.service";
import InputButton from "@/components/input-button/InputButton.vue";

export default {
    name: "VisitsTable",
    components: {
        InputButton,
        InputDropdownCheckbox,
        InputCheckbox,
        InputDatePicker,
        InputDropdown,
        InputText,
        AGDataTable,
        NavigationBar
    },

    data () {
        return {
            gridApi: undefined,
            columnApi: undefined,
            bookings: [],
            filteredBookings: [],
            locations: [],
            dropDownLocations: [],
            selectedLocation: undefined,
            dropDownOptions: [],
            location: undefined,
            startDate: moment().startOf('day').add(0, 'days'),
            endDate: moment().endOf('day').add(0, 'days'),
            showDetails: false,
            entities: [],
            dropDownEntities: [],
            searchValue: undefined,
            customers: [],
            dropDownCustomers: [],
            showAdvancedFilters: false,
            loggedInAccount: undefined,
            filterName: 'visitsTableFilter',
            cookieFilter: undefined
        }
    },

    async mounted() {
        this.locations = await LocationService.getLocations()

        this.groups = await GroupService.getGroups()

        this.dropDownLocations = this.locations.map(l => new LocationDropdown(l))

        this.cookieFilter = CookieService.getCookie(this.filterName)

        let locationId = this?.cookieFilter?.locationId ?? this.locations[0].id

        this.location = await LocationService.getLocation(locationId)
        this.entities = await EntityService.getEntities(this.location.id, this.startDate.toISOString())
        this.dropDownEntities = this.entities.map((e) => {
            e.checked = this.cookieFilter?.entities?.includes(e.id) == undefined ? true : this.cookieFilter?.entities?.includes(e.id)
            return new EntityDropdown(e)
        })

        await this.getCustomersForLocation(this.location.id)

        await this.getBookings()
        await this.getBookingOptions()
        this.updateBookingFilter()
    },

    watch: {
      searchValue(val, old) {
          this.gridApi?.setGridOption('quickFilterText', val)
      }
    },

    computed: {
        columnHeaders () {
            const optionsToShow = this.dropDownOptions.filter(o => o.checked)
            return columnHeaders.visitsColumnHeaders(optionsToShow, this.showDetails)
        },

        filter () {
            return {
                locationId: this.location.id,
                customers: this.dropDownCustomers.filter(c => c.checked).map(c => c.id),
                entities: this.dropDownEntities.filter(e => e.checked).map(e => e.id),
                options: this.dropDownOptions.filter(o => o.checked).map(o => o.id),
            }
        }
    },

    methods: {
        onGridReady (params) {
            this.gridApi = params.api
            this.columnApi = params.columnApi;
        },

        async updateStartDate (date) {
            this.startDate = moment(date).startOf('day')
            await this.getBookings()

        },

        async updateEndDate (date) {
            this.endDate = moment(date).endOf('day')
            await this.getBookings()
        },

        async updateLocation(newLocationId) {
            this.selectedGroup = undefined
            this.location = await LocationService.getLocation(newLocationId)
            this.entities = await EntityService.getEntities(this.location.id, this.startDate.toISOString())
            this.dropDownEntities = this.entities.map((e) => {
                e.checked = true
                return new EntityDropdown(e)
            })
            await this.getBookingOptions()
            await this.getCustomersForLocation(this.location.id)
            await this.getBookings()
            this.filteredBookings = this.bookings

            CookieService.storeCookie(this.filterName, this.filter, 365)
        },

        async getCustomersForLocation (locationId) {
            let customers= await CustomerService.getCustomers()
            if (KeycloakService.isBookingAgent()) {
                if (this.loggedInAccount == undefined) {
                   this.loggedInAccount = await AccountService.getLoggedInAccount()
                }
                const allowedCustomers = this.loggedInAccount?.customers?.map(c => c.id)
                this.customers = customers.filter((customer) => {
                    const locationIds = customer.locations.map(l => l.id)
                    const includesLocation = locationIds.includes(locationId)

                    const allowedCustomer = allowedCustomers.includes(customer.id)

                    return includesLocation && allowedCustomer
                })
            } else {
                this.customers = customers.filter((customer) => {
                    const locationIds = customer.locations.map(l => l.id)
                    return locationIds.includes(locationId)
                })
            }
            this.dropDownCustomers = this.customers.map((c) => {
                c.checked = this.cookieFilter?.customers?.includes(c.id) == undefined ? true : this.cookieFilter?.customers?.includes(c.id)
                return new CustomerDropdown(c)
            })
        },

        async getBookingOptions () {
            let options = await BookingService.getBookingOptions(this.location.id)
            options = options.filter(o => o.usedForVisitsDisplay)
            this.dropDownOptions = options.map((o) => {
                o.checked = this.cookieFilter?.options?.includes(o.id) == undefined ? false : this.cookieFilter?.options?.includes(o.id)
                return new BookingOptionDropDown(o.id, o.name, o.checked)
            })
        },

        async getBookings () {
            if (KeycloakService.isBookingAgent()) {
                if (this.loggedInAccount == undefined) {
                    this.loggedInAccount = await AccountService.getLoggedInAccount()
                }
            }
            return BookingService.getBookingsByLocation(this.location, this.startDate.toISOString(), this.endDate.toISOString()).then((bookings) => {
                return TimezoneService.correctAllBookingsDown(bookings, this.location)
            }).then((correctedBookings) => {
                if(KeycloakService.isBookingAgent() && this.location.bookingsPrivateForAgent) {
                    //filter bookings
                    return correctedBookings.filter(b => b.userCreated == this.loggedInAccount.emailAddress)
                }
                return correctedBookings
            }).then((correctedBookings) => {
                const currentTime = moment()
                return correctedBookings.map((booking) => {
                    const startDate = moment(booking.entity.startDate)
                    const endDate = moment(booking.entity.endDate)

                    const vehicleArrivalDate = booking.vehicleArrivalDate ? moment(booking.vehicleArrivalDate).format('YYYY-MM-DD HH:mm') : undefined
                    const vehicleDepartureDate = booking.vehicleDepartureDate ? moment(booking.vehicleDepartureDate).format('YYYY-MM-DD HH:mm') : undefined
                    const dockArrivalDate  = booking.dockArrivalDate ? moment(booking.dockArrivalDate).format('YYYY-MM-DD HH:mm') : undefined
                    const dockDepartureDate  = booking.dockDepartureDate ? moment(booking.dockDepartureDate).format('YYYY-MM-DD HH:mm') : undefined
                    const startHandlingDate  = booking.startHandlingDate ? moment(booking.startHandlingDate).format('YYYY-MM-DD HH:mm') : undefined
                    const stopHandlingDate  = booking.stopHandlingDate ? moment(booking.stopHandlingDate).format('YYYY-MM-DD HH:mm') : undefined

                    return {
                        id: booking.id,
                        customer: booking.customer.name,
                        customerId: booking.customer.id,
                        entity: booking.entity.name,
                        entityId: booking.entity.id,
                        licensePlate: booking.licensePlate,
                        trailerPlate: booking.trailerPlate,
                        status: this.determineStatus(booking),
                        date: startDate.format('YYYY-MM-DD'),
                        startTime: startDate.format('HH:mm'),
                        endTime: endDate.format('HH:mm'),
                        duration: endDate.diff(startDate, 'minutes'),
                        spots: booking?.spots ? booking.spots.map(s => s.label).join(', ') : undefined,
                        options: booking?.options,
                        vehicleArrivalDate: vehicleArrivalDate,
                        vehicleDepartureDate: vehicleDepartureDate,
                        dockArrivalDate: dockArrivalDate,
                        dockDepartureDate: dockDepartureDate,
                        startHandlingDate: startHandlingDate,
                        stopHandlingDate: stopHandlingDate,
                        isDelayed: currentTime.isAfter(startDate),
                        userCreated: booking.userCreated
                    }
                })
            }).then((bookings) => {
                this.bookings = bookings;
                this.filteredBookings = bookings;
                this.updateBookingFilter()
            }).catch((err) => {
                return []
            })
        },

        determineStatus(booking) {
            if (booking.status.id == 1) {
                return booking.status.name
            } else if (booking?.vehicleArrivalDate != undefined && booking?.vehicleDepartureDate == undefined ) {
                // truck is on yard
                let [dockArrival, dockDeparture, handlingStart, handlingEnd] = [booking?.dockArrivalDate, booking?.dockDepartureDate, booking?.startHandlingDate, booking?.stopHandlingDate]
                    if (dockArrival != undefined && dockDeparture != undefined) {
                        return 'Off Dock'
                    } else if (dockArrival != undefined && handlingStart != undefined && handlingEnd == undefined) {
                        return 'Handling'
                    } else if (dockArrival != undefined && handlingStart != undefined && handlingEnd != undefined) {
                        return 'Handled'
                    } else if (dockArrival != undefined && dockDeparture == undefined) {
                        return 'On Dock'
                    }
                return 'On Yard'
            }  else if (booking?.vehicleDepartureDate != undefined) {
                return 'Departed'
            } else {
                return booking.status.name
            }
        },

        toggleDetails () {
            this.showDetails = !this.showDetails;
            this.gridApi.sizeColumnsToFit()
        },

        updateBookingFilter () {
            const entitiesToShow = this.dropDownEntities.filter(e => e.checked).map(e => e.id)
            const customersToShow = this.dropDownCustomers.filter(c => c.checked).map(c => c.id)
            this.filteredBookings = this.bookings.filter((booking) => {
                const validCustomer = customersToShow.includes(booking.customerId.toString())
                const validEntity = entitiesToShow.includes(booking.entityId.toString())

                return validCustomer && validEntity
            })

            CookieService.storeCookie(this.filterName, this.filter, 365)
        },

        updateOptionsFilter () {
            CookieService.storeCookie(this.filterName, this.filter, 365)
        },

        selectVisit(row) {
            const {single, multiple} = row
            if (single) {
                const visit = single
                router.push(`/booking/${visit.id}`)
            }
        },

        toggleFilters () {
            this.showAdvancedFilters = !this.showAdvancedFilters
        },

        clearFilters () {
            CookieService.clearCookie(this.filterName)
            this.dropDownOptions = this.dropDownOptions.map((o) => {
                o.checked = false
                return o
            })
            this.dropDownEntities = this.dropDownEntities.map((e) => {
                e.checked = true
                return e
            })
            this.dropDownCustomers = this.dropDownCustomers.map((c) => {
                c.checked = true
                return c
            })
            this.updateBookingFilter()
        }
    }
}
